import {Color} from '@benchling/nucleus-ui';
import {createStyles, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import {ONBOARDING_SUBTITLE} from './onboardingCopy';

const useStyles = createStyles((theme, {variant}: {variant: 'invite' | 'survey'}) => {
  const h2LineHeightPx = parseFloat(theme.headings.sizes.h2.lineHeight as string);
  const spacingXs = parseFloat(theme.spacing.xs);
  const fontSizeLg = parseFloat(theme.fontSizes.lg);
  const fontSizeSm = parseFloat(theme.fontSizes.sm);
  const lineHeight = theme.lineHeight as number;

  // survey: 3-line title + stack gap + subtitle line
  const surveyHeight = h2LineHeightPx * 3 + spacingXs + fontSizeLg * lineHeight;
  // invite: 2-line title + gap + subtitle + gap + action row (sm font)
  const inviteMinHeight =
    h2LineHeightPx * 2 + spacingXs + fontSizeLg * lineHeight + spacingXs + fontSizeSm * lineHeight;

  return {
    // Title, subtitle, and optional action share one top-aligned block. Invite always reserves the
    // "Maybe later" row; survey uses a fixed three-line title block so the choices band never shifts.
    headingBlock: {
      textAlign: 'center',
      width: '100%',
      ...(variant === 'survey' ? {height: surveyHeight} : {minHeight: inviteMinHeight}),
    },
    heading: {
      textAlign: 'center',
      paddingBottom: theme.spacing.xxs,
      ...(variant === 'survey'
        ? {
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }
        : {}),
    },
    subtitle: {
      textAlign: 'center',
      color: Color.GRAY_900,
    },
  };
});

interface OnboardingHeadingProps {
  title: string;
  /** Defaults to {@link ONBOARDING_SUBTITLE} so invite and survey screens stay aligned. */
  subtitle?: string;
  /** Optional control centered below the subtitle (e.g. the banner's "Maybe later" link). */
  action?: React.ReactNode;
  /** Survey questions use a fixed three-line title block; the invite keeps extra action-row space. */
  variant?: 'invite' | 'survey';
}

/**
 * Shared onboarding title + subtitle block. Used on the welcome-panel invite and inside
 * {@link OnboardingQuestionView} so typography and the two-line title height stay consistent.
 */
export function OnboardingHeading({
  title,
  subtitle = ONBOARDING_SUBTITLE,
  action,
  variant = 'invite',
}: OnboardingHeadingProps): JSX.Element {
  const {classes} = useStyles({variant});

  return (
    <Stack className={classes.headingBlock} spacing="xs" align="center">
      <Title order={2} className={classes.heading}>
        {title}
      </Title>
      <Text size="lg" className={classes.subtitle}>
        {subtitle}
      </Text>
      {action}
    </Stack>
  );
}
